import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';
import { cn } from '@/lib/cn';
import Spinner from './spinner';
const buttonVariants = cva('inline-flex relative font-semibold items-center justify-center gap-2 whitespace-nowrap rounded-md text-md font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0', {
  variants: {
    variant: {
      default: 'bg-secondary text-white hover:opacity-90',
      destructive: 'bg-red-100 text-red-900 hover:bg-red-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2',
      outline: 'border-2 border-secondary bg-transparent hover:bg-accent hover:text-accent-foreground',
      outline_destructive: 'border-2 border-red-400 text-red-400 bg-transparent hover:text-red-600',
      secondary: 'bg-background text-foreground hover:bg-secondary/80',
      ghost: 'bg-gray-100 enabled:bg-gray-100 enabled:text-gray-700 text-gray-500 enabled:hover:bg-gray-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
      link: 'text-primary underline-offset-4 hover:underline',
      filter: 'bg-transparent text-gray-500 hover:text-primary'
    },
    size: {
      default: 'h-10 px-4 py-2',
      sm: 'h-9 rounded-md px-3',
      lg: 'h-11 rounded-md px-8',
      icon: 'h-10 w-10'
    }
  },
  defaultVariants: {
    variant: 'default',
    size: 'default'
  }
});
const getSpinnerColor = (variant: VariantProps<typeof buttonVariants>['variant']) => {
  if (variant === 'ghost' || variant === 'outline' || variant === 'secondary') return 'black';
  return 'white';
};
export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
}
const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(({
  className,
  variant,
  size,
  asChild = false,
  loading,
  children,
  ...props
}, ref) => {
  const Comp = asChild ? Slot : 'button';
  return <Comp className={cn(buttonVariants({
    variant,
    size,
    className
  }), {
    'cursor-progress': loading
  })} disabled={loading} ref={ref} style={{}} {...props}>
        {loading ? <Spinner color={getSpinnerColor(variant)} /> : children}
      </Comp>;
});
Button.displayName = 'Button';
export { Button, buttonVariants };